import { useCallback } from "react";
import useErrorToast from "~/contexts/error-toast";
import noop from "~/utils/noop";

interface ErrorResponse {
  Message?: string;
}

export const useGlobalErrorHandler = () => {
  const { showError } = useErrorToast();

  return useCallback(
    (error: any) => {
      // Handle 403 PermissionError specifically
      if (error?.status === 403) {
        let message = "You do not have permission to perform this action";

        // Try to extract meaningful error message from response
        if (error.message) {
          try {
            const errorData: ErrorResponse = JSON.parse(error.message);
            message = errorData.Message || message;
          } catch {
            // If parsing fails, use the raw message if it's not JSON
            if (!error.message.startsWith("{")) {
              message = error.message;
            }
          }
        }

        showError(message);
        return;
      }

      // Handle other common error statuses
      if (error?.status >= 400 && error?.status < 500) {
        let message = "An error occurred while processing your request";

        if (error.message) {
          try {
            const errorData: ErrorResponse = JSON.parse(error.message);
            message = errorData.Message || message;
          } catch {
            // If parsing fails, use the raw message if it's not JSON
            if (!error.message.startsWith("{")) {
              message = error.message;
            }
          }
        }

        showError(message);
        return;
      }

      // For 5xx errors, show generic server error
      if (error?.status >= 500) {
        showError("A server error occurred. Please try again later.");
        return;
      }

      // Fallback for unknown errors
      showError("An unexpected error occurred");
    },
    [showError]
  );
};
